import React, { cloneElement, isValidElement, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import './styles.scss'

/**
 * IMPORTANT: If 'children' is a custom React component (not a standard HTML element),
 * it MUST forward its props to the underlying DOM element (usually via {...props}).
 * * Why? This wrapper uses cloneElement to inject 'data-tooltip-id'. If the custom
 * component doesn't spread this attribute onto its root HTML tag, the tooltip
 * will have no anchor point and won't appear.
 */
const TooltipWrapper = ({
  content,
  place,
  className,
  classNameArrow,
  id,
  delayShow,
  delayHide,
  clickable,
  noArrow,
  offset,
  arrowSize,
  bgColor,
  maxWidth,
  positionStrategy,
  children,
  hidden,
}) => {
  const [tooltipId] = useState(() => id || `tooltip-${uuidv4()}`)

  if (process.env.NODE_ENV !== 'production' && !isValidElement(children)) {
    console.error('TooltipWrapper: `children` must be a single React element.')
  }

  const style = {
    '--tooltip-bg': bgColor,
    '--tooltip-max-width': maxWidth,
  };

  return (
    <>
      {isValidElement(children) && cloneElement(children, {
        'data-tooltip-id': tooltipId
      })}

      <Tooltip
        id={tooltipId}
        place={place}
        className={classNames('TooltipWrapper', className)}
        classNameArrow={classNameArrow}
        delayShow={delayShow}
        delayHide={delayHide}
        clickable={clickable}
        noArrow={noArrow}
        offset={offset}
        arrowSize={arrowSize}
        positionStrategy={positionStrategy}
        style={style}
        hidden={hidden}
      >
        {content}
      </Tooltip>
    </>
  )
}

TooltipWrapper.defaultProps = {
  place: 'top',
  positionStrategy: 'absolute',
  hidden: false,
}

TooltipWrapper.propTypes = {
  content: PropTypes.node.isRequired,
  place: PropTypes.oneOf([
    'top', 'top-start', 'top-end',
    'right', 'right-start', 'right-end',
    'bottom', 'bottom-start', 'bottom-end',
    'left', 'left-start', 'left-end'
  ]),
  className: PropTypes.string,
  classNameArrow: PropTypes.string,
  id: PropTypes.string,
  delayShow: PropTypes.number,
  delayHide: PropTypes.number,
  clickable: PropTypes.bool,
  noArrow: PropTypes.bool,
  offset: PropTypes.number,
  arrowSize: PropTypes.number,
  bgColor: PropTypes.string,
  maxWidth: PropTypes.string,
  /** Use 'fixed' to prevent clipping when inside containers with overflow: hidden or scroll. */
  positionStrategy: PropTypes.oneOf(['absolute', 'fixed']),
  children: PropTypes.element.isRequired,
  hidden: PropTypes.bool,
}

export default TooltipWrapper
